<template>
  <div>
    <!-- CountDown Messages -->
    <b-alert
      class="alert-message-tc text-center"
      :class="message.classes"
      :show="message.countDown"
      dismissible
      fade
      :variant="message.status"
      @dismissed="clearCountDown"
      @dismiss-count-down="updateCountDown"
    >
      <div class="message">
        <span class="medium" id="statusMessage" v-html="message.text"></span>
      </div>
      <button
        v-if="message.button && message.button.show"
        :class="message.button.classes"
        @click="handleClick"
      >
        {{ message.button.text }}
      </button>
    </b-alert>

    <!-- NoCountDown Messages -->
    <b-alert
      class="alert-message-tc"
      :class="message.classes"
      :show="message.display"
      dismissible
      fade
      :variant="message.status"
      @dismissed="handleDismiss"
    >
      <div class="message-content d-flex justify-content-center text-center">
        <div
          class="icon"
          :style="{ 'background-image': `url(${message.icon})` }"
        ></div>
        <div class="message" v-html="message.text"></div>
        <button
          v-if="message.button && message.button.show"
          :class="message.button.classes"
          @click="handleClick"
        >
          {{ message.button.text }}
        </button>
      </div>
    </b-alert>
  </div>
</template>

<script>
import { LOW, HIGH } from '@/helpers/messages-priority'
import { of, iif, Observable } from 'rxjs'
import { mergeMap, map, tap } from 'rxjs/operators'

const originalMessage = {
  id: undefined,
  countDown: 0,
  display: false,
  icon: undefined,
  seconds: 5,
  status: undefined,
  text: undefined,
  button: undefined,
  classes: '',
  dismissClick: () => {},
  ignoreBeforeTransition: false,
  priority: LOW,
}

const messageIcons = {
  group: '/messages/icon_group.svg',
  network: '/messages/icon_network.svg',
}

export default {
  data() {
    return {
      message: originalMessage,
      queue: [],
    }
  },
  watch: {
    queue() {
      if (this.queue.length >= 1 && this.queue[0].show === false) {
        this.queue[0].show = true
        setTimeout(() => {
          this.message = this.queue[0].message
        }, 100)
      }
    },
  },
  created() {
    this.$root.$on('app:message:display', message => {
      message = {
        ...originalMessage,
        ...message,
        icon: messageIcons[message.icon],
        display: true,
      }
      this.onMessageShow(message)
    })
    this.$root.$on('app:message:show', message => {
      message = {
        ...originalMessage,
        ...message,
        countDown: originalMessage.seconds,
      }
      this.onMessageShow(message)
    })

    this.$root.$on('app:message:hideOnBeforeDestroy', this.hideOnBeforeDestroy)
    this.$root.$on('app:message:hide', this.hide)
  },
  methods: {
    updateCountDown(countDown) {
      this.message.countDown = countDown
    },

    clearCountDown() {
      this.message.dismissClick.apply(this, ...arguments)
      this.message.countDown = 0
      this.hide()
    },
    handleClick() {
      this.message.button.click.apply(this, ...arguments)
    },
    handleDismiss() {
      this.message.dismissClick.apply(this, ...arguments)
      this.hide()
    },
    hide() {
      this.message = originalMessage
      this.queue.splice(0, 1)
    },
    hideOnBeforeDestroy() {
      !this.message.ignoreBeforeTransition && this.hide()
    },
    isRepeatedMessage({ id, text }) {
      return (
        this.queue.filter(
          ({ message }) =>
            message.text === text || (message.id === id && id !== undefined)
        ).length > 0
      )
    },
    onMessageShow(message) {
      const pushMessage$ = (message, queue) =>
        of(queue).pipe(tap(() => queue.push(message)))

      const unshiftMessage$ = data =>
        Observable.create(() => {
          /* istanbul ignore next */
          this.queue = this.queue.map(data => ({ ...data, show: false }))
          this.queue = [data, ...this.queue]
        })

      const overwriteMessage = message =>
        Observable.create(() => {
          this.queue = this.queue.map(data => {
            return (data.message.id === message.id &&
              message.id !== undefined) ||
              data.message.text === message.text
              ? { ...data, message }
              : data
          })
        })

      const addMessage = message =>
        of(message).pipe(
          map(message => ({ show: false, message })),
          mergeMap(data =>
            iif(
              () => data.message.priority === HIGH,
              unshiftMessage$(data),
              pushMessage$(data, this.queue)
            )
          )
        )

      of(message)
        .pipe(
          mergeMap(message =>
            iif(
              () => this.isRepeatedMessage(message),
              overwriteMessage(message),
              addMessage(message)
            )
          )
        )
        .subscribe()
    },
  },
}
</script>

<style scoped lang="scss">
$default-height: 55px;
@import '~@/styles/custom/colors';

.alert-message-tc {
  min-height: 70px;
  padding: 5px 35px 5px;
  z-index: 10;

  @media (min-width: 1024px) {
    padding: 10px 60px 10px;
  }

  /deep/ button.close {
    color: #000000 !important;
    padding: 0 !important;
    top: 6px;
    right: 20px;
    height: 20px !important;

    @media (min-width: 1024px) {
      border-radius: 32px;
      border: 1px solid map-get($theme-colors, 'gray03');
      margin: 0;
      min-width: 57px;
      min-height: $default-height;
      height: auto;
    }
  }

  /deep/ strong,
  /deep/ .message {
    font-size: 14px;

    @media (min-width: 425px) {
      font-size: 16px;
    }

    @media (min-width: 1024px) {
      font-size: 20px;
      padding-top: 6px;
    }
  }
}
.icon {
  background-repeat: no-repeat;
  min-width: 50px;
  max-width: 50px;

  @media (max-width: 400px) {
    min-width: 35px;
    max-width: 35px;
  }
}
.message {
  padding: 0 15px;
}
</style>
